import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Shield, Building2, Layers, MapPinned, Users2, ClipboardList, Sun, Moon, LogOut, Menu, X, LayoutDashboard, Ticket, Megaphone, Users, CreditCard, Receipt, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";

const LOGO_URL = "https://customer-assets-0z36b82j.emergentagent.net/job_ticketing-transport/artifacts/5p6cedk3_1000607872.png";

const NAV = [
  { to: "/superadmin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/superadmin/companies", label: "Entreprises", icon: Building2 },
  { to: "/superadmin/users", label: "Utilisateurs", icon: Users },
  { to: "/superadmin/subscriptions", label: "Abonnements", icon: CreditCard },
  { to: "/superadmin/transactions", label: "Transactions", icon: Receipt },
  { to: "/superadmin/payment-providers", label: "API Paiement", icon: KeyRound },
  { to: "/superadmin/tickets", label: "Billets", icon: Ticket },
  { to: "/superadmin/plans", label: "Plans tarifaires", icon: Layers },
  { to: "/superadmin/city-pricing", label: "Prix par ville", icon: MapPinned },
  { to: "/superadmin/super-admins", label: "Super Admins", icon: Users2 },
  { to: "/superadmin/announcements", label: "Annonces", icon: Megaphone },
  { to: "/superadmin/audit", label: "Journal d'audit", icon: ClipboardList },
];

export default function SuperAdminLayout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <aside className="hidden md:flex md:w-64 flex-col fixed inset-y-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 z-30">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-slate-200 dark:border-slate-800">
          <div className="w-10 h-10 rounded-xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 grid place-items-center">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-bold text-sm">Super Admin</div>
            <div className="text-[11px] uppercase tracking-widest text-orange-500">Transport.pro</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <NavLink key={to} to={to} end={end} data-testid={`sa-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 shadow-sm"
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                }`}>
              <Icon className="w-4 h-4" /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200 dark:border-slate-800 text-sm">
          <div className="font-semibold truncate">{user?.full_name}</div>
          <div className="text-xs text-slate-500">{user?.email}</div>
          <Button variant="outline" size="sm" onClick={handleLogout} className="w-full mt-2" data-testid="sa-logout"><LogOut className="w-3.5 h-3.5 mr-1" /> Déconnexion</Button>
        </div>
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setOpen(false)}>
          <div className="w-72 h-full bg-white dark:bg-slate-900 p-3 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 px-2">
              <div className="font-display font-bold">Super Admin</div>
              <Button variant="ghost" size="icon" onClick={() => setOpen(false)}><X className="w-4 h-4" /></Button>
            </div>
            {NAV.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "bg-slate-900 text-white" : "text-slate-700 dark:text-slate-200"}`}>
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 md:pl-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 glass sticky top-0 z-20 flex items-center px-4 md:px-6 gap-3">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setOpen(true)}><Menu className="w-5 h-5" /></Button>
          <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
            <Shield className="w-4 h-4" />
            <span>Console propriétaire du SaaS</span>
          </div>
          <div className="ml-auto">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="sa-theme-btn">
              {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 relative z-10 p-4 md:p-8 max-w-[1400px] w-full mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
